"use client";

import React from "react";
import {
  Slider as SliderRoot,
  SliderTrack,
  SliderRange,
  SliderThumb,
} from "@radix-ui/react-slider";
import { cn } from "@/shared/libs/commonUtils";
import { isObject, isString } from "@/shared/libs/type-utils";
import Typography from "./Typography";

interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderRoot> {
  classNames?: {
    root?: string;
    track?: string;
    range?: string;
    thumb?:
      | string
      | {
          root?: string;
          values?: string;
        };
  };
  showValues?: "above" | "below" | false;
  valueFormat?: (value: number) => string;
}

const Slider = React.forwardRef<
  React.ComponentRef<typeof SliderRoot>,
  SliderProps
>(
  (
    {
      className,
      classNames = {},
      showValues = false,
      valueFormat = (value) => value.toString(),
      min = 0,
      max = 100,
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const values = value || defaultValue || [min, max];
    const [minValue, maxValue] = values;

    const thumbClassName = cn(
      "relative block h-5 w-5 rounded-full border border-primary/50 bg-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
      showValues ? "flex flex-col items-center" : "",
      isString(classNames.thumb) ? classNames.thumb : classNames.thumb?.root
    );

    const renderThumb = (index: number, position: number) => (
      <SliderThumb className={thumbClassName}>
        {showValues === "above" && (
          <Typography
            variant={"body3"}
            className={cn(
              "absolute -top-5 text-xs text-muted-foreground pointer-events-none text-pretty",
              isObject(classNames.thumb) ? classNames.thumb?.values : ""
            )}
          >
            {valueFormat(position)}
          </Typography>
        )}
        {showValues === "below" && (
          <Typography
            variant={"body3"}
            className={cn(
              "absolute top-5 text-xs text-muted-foreground pointer-events-none text-nowrap",
              isObject(classNames.thumb) ? classNames.thumb?.values : ""
            )}
          >
            {valueFormat(position)}
          </Typography>
        )}
      </SliderThumb>
    );

    return (
      <SliderRoot
        ref={ref}
        className={cn(
          "relative flex w-full touch-none select-none items-center",
          className,
          classNames.root
        )}
        min={min}
        max={max}
        value={value}
        defaultValue={defaultValue}
        {...props}
      >
        <SliderTrack
          className={cn(
            "relative h-0.5 w-full grow overflow-hidden rounded-full bg-muted",
            classNames.track
          )}
        >
          <SliderRange
            className={cn("absolute h-full bg-primary", classNames.range)}
          />
        </SliderTrack>
        {renderThumb(0, minValue)}
        {renderThumb(1, maxValue)}
      </SliderRoot>
    );
  }
);

Slider.displayName = SliderRoot.displayName;

export default Slider;
